// frontend/src/components/UsageDashboard/UsageDashboard.js
import React, { useEffect, useState } from 'react';
import apiClient from '../../apiClient';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import './UsageDashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function UsageDashboard() {
  // -- State för aktiv flik
  const [activeTab, setActiveTab] = useState('day'); // 'day' | 'clinic' | 'minutes'

  // -- Transkriberade minuter (idag / 7 dagar / 30 dagar)
  const [minutesStats, setMinutesStats] = useState(null);

  // -- Clinics och klinikval
  const [clinics, setClinics] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState(0);

  // -- Dagöversikt-data
  const [dayRange, setDayRange] = useState('today'); // 'today' | 'week' | 'month'
  const [todayCount, setTodayCount] = useState(0);
  const [hourlyData, setHourlyData] = useState([]);

  // -- Klinikstatistik-data
  const [dailyStats, setDailyStats] = useState([]);
  const [usersWithStats, setUsersWithStats] = useState([]);

  // -- Loading & error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ============================================================================
  // Hämta clinics vid mount
  // ============================================================================
  const fetchClinics = async () => {
    try {
      const resp = await apiClient.get('/api/clinics');
      setClinics(resp.data);
    } catch (err) {
      console.error('Fel vid hämtning av clinics:', err);
    }
  };

  // ============================================================================
  // Dagöversikt: Hämta timvis data
  // ============================================================================
  const fetchHourlyData = async (range = 'today') => {
    try {
      const resp = await apiClient.get(`/api/stats/hourly_today?range=${range}`);
      setHourlyData(resp.data.hourly);
      setTodayCount(resp.data.total);
    } catch (err) {
      console.error('Fel vid hämtning av hourly data:', err);
    }
  };

  // ============================================================================
  // Minuter: Hämta transkriberade minuter (idag / 7 dagar / 30 dagar)
  // ============================================================================
  const fetchMinutesStats = async () => {
    try {
      const resp = await apiClient.get('/api/stats/transcription_minutes');
      setMinutesStats(resp.data);
    } catch (err) {
      console.error('Fel vid hämtning av transkriberade minuter:', err);
    }
  };

  // ============================================================================
  // Klinikstatistik: Hämta daglig statistik senaste 30 dagarna
  // ============================================================================
  const fetchDailyStats = async () => {
    setLoading(true);
    try {
      let url = '/api/stats/daily';
      if (selectedClinic !== 0) {
        url += `?clinic_id=${selectedClinic}`;
      }
      const resp = await apiClient.get(url);
      // Aggregera per datum (summera user_id-rader)
      const byDate = {};
      resp.data.forEach((row) => {
        if (!byDate[row.date]) {
          byDate[row.date] = 0;
        }
        byDate[row.date] += row.total_journals || 0;
      });

      // Konvertera till array och sortera
      const arr = Object.entries(byDate).map(([date, count]) => ({ date, count }));
      arr.sort((a, b) => new Date(a.date) - new Date(b.date));

      // Behåll senaste 30 dagarna
      setDailyStats(arr.slice(-30));
    } catch (err) {
      console.error('Fel vid hämtning av daily stats:', err);
      setError('Kunde inte hämta daglig statistik.');
    }
    setLoading(false);
  };

  // ============================================================================
  // Klinikstatistik: Hämta användare med stats
  // ============================================================================
  const fetchUsersWithStats = async () => {
    try {
      let url = '/api/users/with_stats';
      if (selectedClinic !== 0) {
        url += `?clinic_id=${selectedClinic}`;
      }
      const resp = await apiClient.get(url);
      setUsersWithStats(resp.data);
    } catch (err) {
      console.error('Fel vid hämtning av users with stats:', err);
    }
  };

  // ============================================================================
  // Initial mount
  // ============================================================================
  useEffect(() => {
    fetchClinics();
    fetchHourlyData('today');
  }, []);

  // ============================================================================
  // När dayRange ändras, hämta ny data
  // ============================================================================
  useEffect(() => {
    fetchHourlyData(dayRange);
    // eslint-disable-next-line
  }, [dayRange]);

  // ============================================================================
  // När selectedClinic ändras, hämta klinikdata
  // ============================================================================
  useEffect(() => {
    if (activeTab === 'clinic') {
      fetchDailyStats();
      fetchUsersWithStats();
    }
    // eslint-disable-next-line
  }, [selectedClinic, activeTab]);

  // ============================================================================
  // När minuter-fliken öppnas, hämta data
  // ============================================================================
  useEffect(() => {
    if (activeTab === 'minutes') {
      fetchMinutesStats();
    }
    // eslint-disable-next-line
  }, [activeTab]);

  // ============================================================================
  // Formatera minuter: heltalsminuter + " ≈ X tim Y min" när det är > 60 min
  // ============================================================================
  const formatMinutes = (minutes) => {
    const total = Math.round(minutes || 0);
    if (total < 60) {
      return { value: total, sub: null };
    }
    const hours = Math.floor(total / 60);
    const rest = total % 60;
    return { value: total, sub: `≈ ${hours} tim ${rest} min` };
  };

  // ============================================================================
  // Handlers
  // ============================================================================
  const handleClinicSelect = (e) => {
    setSelectedClinic(parseInt(e.target.value, 10));
  };

  // ============================================================================
  // CSV Export
  // ============================================================================
  const handleExportCSV = () => {
    // Exportera användarstatistik
    let csv = 'user_id,first_name,last_name,clinic_id,total_journals\n';
    usersWithStats.forEach((u) => {
      csv += `${u.user_id},"${u.first_name}","${u.last_name}",${u.clinic_id},${u.total_journals}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'usage-stats.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ============================================================================
  // Chart: Timvis stapeldiagram för Dagöversikt
  // ============================================================================
  const prepareHourlyChart = () => {
    // Fokusera på arbetstid 07:00-19:00
    const workHours = hourlyData.filter(h => h.hour >= 7 && h.hour <= 19);

    return {
      labels: workHours.map(h => `${String(h.hour).padStart(2, '0')}:00`),
      datasets: [{
        label: 'Journaler',
        data: workHours.map(h => h.count),
        backgroundColor: 'rgba(54, 162, 235, 0.7)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      }]
    };
  };

  // ============================================================================
  // Chart: Linjediagram för klinikstatistik (senaste 30 dagar)
  // ============================================================================
  const prepareDailyChart = () => {
    return {
      labels: dailyStats.map(d => d.date),
      datasets: [{
        label: 'Journaler per dag',
        data: dailyStats.map(d => d.count),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1,
        fill: true
      }]
    };
  };

  // ============================================================================
  // Beräkna snitt/arbetsdag per användare
  // ============================================================================
  const calculateUserAverages = () => {
    // Hämta antal unika arbetsdagar per användare från dailyStats (via /api/stats/daily)
    // För enkelhetens skull räknar vi på totalt / 30 (senaste 30 dagarna)
    return usersWithStats.map(u => {
      const avgPerDay = dailyStats.length > 0 ? (u.total_journals / 30).toFixed(1) : 0;
      return { ...u, avgPerDay };
    }).sort((a, b) => b.total_journals - a.total_journals);
  };

  // ============================================================================
  // Dag-för-dag matris (senaste 7 dagarna)
  // ============================================================================
  const [userDailyMatrix, setUserDailyMatrix] = useState({});
  const [matrixDates, setMatrixDates] = useState([]);

  const fetchUserDailyMatrix = async () => {
    try {
      let url = '/api/stats/daily';
      if (selectedClinic !== 0) {
        url += `?clinic_id=${selectedClinic}`;
      }
      const resp = await apiClient.get(url);

      // Hitta senaste 7 dagarna
      const today = new Date();
      const dates = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        dates.push(d.toISOString().split('T')[0]);
      }
      setMatrixDates(dates);

      // Bygg matris: { user_id: { date: count } }
      const matrix = {};
      resp.data.forEach((row) => {
        if (!matrix[row.user_id]) {
          matrix[row.user_id] = {};
        }
        matrix[row.user_id][row.date] = row.total_journals || 0;
      });
      setUserDailyMatrix(matrix);
    } catch (err) {
      console.error('Fel vid hämtning av user daily matrix:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'clinic') {
      fetchUserDailyMatrix();
    }
    // eslint-disable-next-line
  }, [selectedClinic, activeTab]);

  // ============================================================================
  // Summering för klinikstatistik
  // ============================================================================
  const getClinicSummary = () => {
    const total = dailyStats.reduce((sum, d) => sum + d.count, 0);
    const avg = dailyStats.length > 0 ? (total / dailyStats.length).toFixed(1) : 0;
    return { total, avg };
  };

  const clinicSummary = getClinicSummary();
  const userAverages = calculateUserAverages();

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className="usage-dashboard-container">
      <h2>Användningsstatistik</h2>
      {error && <p className="error-message">{error}</p>}

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'day' ? 'active' : ''}`}
          onClick={() => setActiveTab('day')}
        >
          Dagöversikt
        </button>
        <button
          className={`tab ${activeTab === 'clinic' ? 'active' : ''}`}
          onClick={() => setActiveTab('clinic')}
        >
          Klinikstatistik
        </button>
        <button
          className={`tab ${activeTab === 'minutes' ? 'active' : ''}`}
          onClick={() => setActiveTab('minutes')}
        >
          Transkriberade minuter
        </button>
      </div>

      {/* ====================================================================== */}
      {/* FLIK 1: Dagöversikt */}
      {/* ====================================================================== */}
      {activeTab === 'day' && (
        <div className="tab-content">
          {/* Datumväljare */}
          <div className="day-range-selector">
            <button
              className={dayRange === 'today' ? 'active' : ''}
              onClick={() => setDayRange('today')}
            >
              Idag
            </button>
            <button
              className={dayRange === 'week' ? 'active' : ''}
              onClick={() => setDayRange('week')}
            >
              Senaste 7 dagarna
            </button>
            <button
              className={dayRange === 'month' ? 'active' : ''}
              onClick={() => setDayRange('month')}
            >
              Senaste 30 dagarna
            </button>
          </div>

          {/* Stort sammanfattningskort */}
          <div className="day-summary-card">
            <div className="day-summary-value">{todayCount}</div>
            <div className="day-summary-label">
              {dayRange === 'today' && 'Journaler genererade idag'}
              {dayRange === 'week' && 'Journaler senaste 7 dagarna'}
              {dayRange === 'month' && 'Journaler senaste 30 dagarna'}
            </div>
          </div>

          {/* Timvis stapeldiagram */}
          <div className="hourly-chart-box">
            <h3>
              Timvis fördelning (07:00-19:00)
              {dayRange !== 'today' && <span className="chart-subtitle"> – aggregerat</span>}
            </h3>
            {hourlyData.length > 0 ? (
              <Bar
                data={prepareHourlyChart()}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false
                    },
                    title: {
                      display: false
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        stepSize: dayRange === 'today' ? 1 : undefined
                      }
                    }
                  }
                }}
              />
            ) : (
              <p className="no-data">Ingen data ännu</p>
            )}
          </div>
        </div>
      )}

      {/* ====================================================================== */}
      {/* FLIK 2: Klinikstatistik */}
      {/* ====================================================================== */}
      {activeTab === 'clinic' && (
        <div className="tab-content">
          {/* Klinikväljare */}
          <div className="clinic-filter">
            <label htmlFor="clinicSelect"><strong>Välj klinik:</strong></label>
            <select id="clinicSelect" value={selectedClinic} onChange={handleClinicSelect}>
              <option value="0">Alla kliniker</option>
              {clinics.map((c) => (
                <option key={c.clinic_id} value={c.clinic_id}>
                  {c.clinic_name}
                </option>
              ))}
            </select>
            <button className="export-button" onClick={handleExportCSV}>
              Exportera CSV
            </button>
          </div>

          {loading ? (
            <p className="loading-text">Laddar statistik...</p>
          ) : (
            <>
              {/* Sammanfattningskort */}
              <div className="clinic-summary-cards">
                <div className="summary-card">
                  <div className="card-value">{clinicSummary.total}</div>
                  <div className="card-label">Totalt (30 dagar)</div>
                </div>
                <div className="summary-card">
                  <div className="card-value">{clinicSummary.avg}</div>
                  <div className="card-label">Snitt per dag</div>
                </div>
              </div>

              {/* Linjediagram: Journaler per dag senaste 30 dagarna */}
              <div className="daily-chart-box">
                <h3>Journaler per dag (senaste 30 dagarna)</h3>
                {dailyStats.length > 0 ? (
                  <Line
                    data={prepareDailyChart()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true
                        }
                      }
                    }}
                  />
                ) : (
                  <p className="no-data">Ingen data att visa</p>
                )}
              </div>

              {/* Användarstatistik-tabell */}
              <h3>Användarstatistik</h3>
              <table className="usage-stats-table">
                <thead>
                  <tr>
                    <th>Användare</th>
                    <th>Snitt/dag (30d)</th>
                    <th>Totalt</th>
                  </tr>
                </thead>
                <tbody>
                  {userAverages.map((u) => (
                    <tr key={u.user_id}>
                      <td>{u.first_name} {u.last_name}</td>
                      <td>{u.avgPerDay}</td>
                      <td>{u.total_journals}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Dag-för-dag matris */}
              <h3>Senaste 7 dagarna per användare</h3>
              <div className="user-daily-matrix-container">
                <table className="user-daily-matrix">
                  <thead>
                    <tr>
                      <th>Användare</th>
                      {matrixDates.map((date) => (
                        <th key={date}>{date.slice(5)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {usersWithStats
                      .filter(u => userDailyMatrix[u.user_id])
                      .sort((a, b) => b.total_journals - a.total_journals)
                      .map((u) => (
                        <tr key={u.user_id}>
                          <td>{u.first_name} {u.last_name}</td>
                          {matrixDates.map((date) => (
                            <td key={date} className={userDailyMatrix[u.user_id]?.[date] > 0 ? 'has-data' : ''}>
                              {userDailyMatrix[u.user_id]?.[date] || 0}
                            </td>
                          ))}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ====================================================================== */}
      {/* FLIK 3: Transkriberade minuter */}
      {/* ====================================================================== */}
      {activeTab === 'minutes' && (
        <div className="tab-content">
          {minutesStats ? (
            <div className="minutes-cards">
              {[
                { key: 'today', label: 'Idag' },
                { key: 'week', label: 'Senaste 7 dagarna' },
                { key: 'month', label: 'Senaste 30 dagarna' },
              ].map(({ key, label }) => {
                const fmt = formatMinutes(minutesStats[key]?.minutes);
                return (
                  <div key={key} className="minutes-card">
                    <div className="minutes-value">{fmt.value.toLocaleString('sv-SE')}</div>
                    <div className="minutes-unit">minuter</div>
                    {fmt.sub && <div className="minutes-sub">{fmt.sub}</div>}
                    <div className="minutes-label">{label}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="loading-text">Laddar transkriberade minuter...</p>
          )}
          <p className="minutes-note">
            Summan av inspelningarnas längd för genomförda sessioner. Intervallen
            är inklusive idag (7 respektive 30 kalenderdagar bakåt).
          </p>
        </div>
      )}
    </div>
  );
}

export default UsageDashboard;
